import {
  Box,
  Button,
  Card,
  Menu,
  MenuItem,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DropdownTrigger, { ACTIVE_BG, ACTIVE_BORDER, ACTIVE_FG, BORDER, ChevronDown, TEXT_DEFAULT, TEXT_MUTED } from 'Components/FilterDropdownTrigger';
import { DateRange, type Range, type RangeKeyDict } from 'react-date-range';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Controller, type UseFormReturn } from 'react-hook-form';
import coursesApi from 'Api/axios/courses-api';
import type { SessionsReportResponseI } from 'Api/axios/sessions-report-api';
import useTranslation from 'Hooks/useTranslation';
import {
  sessionsReportFilterDefaults,
  type SessionsReportFilterValues,
} from '../schemas/sessions-report-schemas';

interface Props {
  form: UseFormReturn<SessionsReportFilterValues>;
  onChange: (values: SessionsReportFilterValues) => void;
  report: SessionsReportResponseI | null;
  searchPlaceholder?: string;
  hideGroupBy?: boolean;
}

// ─── Tokens specific to this component ────────────────────────────────────
const TEXT_STRONG = '#1F2A3A';

// ─── Icons (inline SVG to match the design exactly) ────────────────────────
type IconProps = { size?: number; color?: string };

const IconSearch = ({ size = 15, color = TEXT_MUTED }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.5-4.5" />
  </svg>
);

const IconCourse = ({ size = 14, color = TEXT_MUTED }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="3" y="5" width="18" height="11" rx="1.5" />
    <path d="M2 20h20" />
  </svg>
);

const IconStatus = ({ size = 14, color = TEXT_MUTED }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M5 3v18M5 4h13l-3 4 3 4H5" />
  </svg>
);

const IconCalendar = ({ size = 15, color = TEXT_MUTED }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </svg>
);

// ─── Options ───────────────────────────────────────────────────────────────
const GROUP_OPTIONS: Array<{ value: NonNullable<SessionsReportFilterValues['groupBy']>; labelKey: string }> = [
  { value: 'course', labelKey: 'groupBy.course' },
  { value: 'month', labelKey: 'groupBy.month' },
  { value: 'instructor', labelKey: 'groupBy.instructor' },
  { value: 'none', labelKey: 'groupBy.none' },
];

const STATUS_OPTIONS: Array<{ value: NonNullable<SessionsReportFilterValues['status']>; labelKey: string }> = [
  { value: 'pending', labelKey: 'filters.statusPending' },
  { value: 'in_progress', labelKey: 'filters.statusInProgress' },
  { value: 'completed', labelKey: 'filters.statusCompleted' },
];

// ─── Styles ────────────────────────────────────────────────────────────────
const styles = {
  card: {
    p: 2,
    mb: 2,
    borderRadius: '12px',
    border: `1px solid ${BORDER}`,
    boxShadow: 'none',
    bgcolor: '#fff',
  } as SxProps<Theme>,
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    flexWrap: 'wrap',
  } as SxProps<Theme>,
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: '9px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    flex: '1 1 260px',
    maxWidth: 320,
    bgcolor: '#fff',
  } as SxProps<Theme>,
  searchInput: {
    border: 'none',
    outline: 'none',
    fontSize: 13,
    flex: 1,
    color: TEXT_STRONG,
    background: 'transparent',
    width: '100%',
    fontFamily: 'inherit',
  } as const,
  dropdownBtn: (active = false): SxProps<Theme> => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: '14px',
    py: '9px',
    borderRadius: '8px',
    border: `1px solid ${active ? ACTIVE_BORDER : BORDER}`,
    bgcolor: active ? ACTIVE_BG : '#fff',
    color: active ? ACTIVE_FG : TEXT_DEFAULT,
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'none',
    minHeight: 0,
    lineHeight: 1.2,
    '&:hover': { bgcolor: active ? ACTIVE_BG : '#fafafa' },
  }),
  dropdownLabel: { color: TEXT_MUTED, fontSize: 12.5 },
  dropdownValue: { fontWeight: 600 },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: TEXT_MUTED,
    fontSize: 12.5,
    cursor: 'pointer',
    textDecoration: 'underline',
    p: 0,
    fontFamily: 'inherit',
  } as const,
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.75,
    mt: '14px',
    pt: '14px',
    borderTop: `1px solid ${BORDER}`,
    flexWrap: 'wrap',
  } as SxProps<Theme>,
  subLabel: { fontSize: 12.5, color: TEXT_MUTED, fontWeight: 500 },
  pill: (active: boolean): SxProps<Theme> => ({
    px: 1.5,
    py: 0.75,
    minWidth: 0,
    minHeight: 0,
    borderRadius: '6px',
    bgcolor: active ? ACTIVE_BG : 'transparent',
    color: active ? ACTIVE_FG : TEXT_DEFAULT,
    fontSize: 12.5,
    fontWeight: active ? 600 : 500,
    textTransform: 'none',
    '&:hover': { bgcolor: active ? ACTIVE_BG : 'rgba(0,0,0,0.04)' },
  }),
  count: { fontSize: 13, fontWeight: 600, color: TEXT_STRONG },
};

const useCourseOptions = () => {
  const [courses, setCourses] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await coursesApi.readAll({ pageSize: 200 });
      if (!cancelled && response.data) {
        setCourses(
          (response.data as Array<{ id: string; name: string }>).map(c => ({ id: c.id, name: c.name }))
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return courses;
};

// ─── Component ─────────────────────────────────────────────────────────────
export default function SessionsReportFilters({ form, onChange, report, searchPlaceholder, hideGroupBy }: Props) {
  const { t2 } = useTranslation('SessionsReport');
  const { control, watch, setValue, reset, handleSubmit } = form;
  const courses = useCourseOptions();

  const currentSearch = watch('search');
  const currentCourseId = watch('courseId');
  const currentStatus = watch('status');
  const currentStartDate = watch('startDate');
  const currentEndDate = watch('endDate');
  const currentGroup = watch('groupBy');

  const submit = useCallback(
    (values: SessionsReportFilterValues) => onChange(values),
    [onChange]
  );

  const handleSelectChange = (field: keyof SessionsReportFilterValues, value: unknown) => {
    setValue(field, value as never);
    handleSubmit(submit)();
  };

  // Debounce search input — refetches automatically after the user stops typing.
  // Skip the initial mount so we don't double-fetch (sessions.tsx fires the first
  // fetch via its own effect with the default values).
  const lastSearchRef = useRef<string | undefined>(currentSearch);
  useEffect(() => {
    if (lastSearchRef.current === currentSearch) return;
    const timer = setTimeout(() => {
      lastSearchRef.current = currentSearch;
      handleSubmit(submit)();
    }, 350);
    return () => clearTimeout(timer);
  }, [currentSearch, handleSubmit, submit]);

  // Per-dropdown anchor elements
  const [courseAnchor, setCourseAnchor] = useState<HTMLElement | null>(null);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);

  const courseLabel = useMemo(() => {
    if (!currentCourseId) return t2('filters.allCourses') as string;
    return courses.find(c => c.id === currentCourseId)?.name ?? (t2('filters.allCourses') as string);
  }, [currentCourseId, courses, t2]);

  const statusLabel = useMemo(() => {
    if (!currentStatus) return t2('filters.allStatuses') as string;
    const opt = STATUS_OPTIONS.find(o => o.value === currentStatus);
    return opt ? (t2(opt.labelKey) as string) : (t2('filters.allStatuses') as string);
  }, [currentStatus, t2]);

  const [draftRange, setDraftRange] = useState<Range>({
    startDate: currentStartDate ?? new Date(),
    endDate: currentEndDate ?? new Date(),
    key: 'selection',
  });

  const handleDateRangeChange = (item: RangeKeyDict) => {
    setDraftRange(item.selection);
  };

  const handleApplyDateRange = () => {
    setValue('startDate', draftRange.startDate);
    setValue('endDate', draftRange.endDate);
    handleSubmit(submit)();
    setCalendarOpen(false);
  };

  const dateRangeLabel = useMemo(() => {
    if (!currentStartDate || !currentEndDate) return t2('filters.selectDateRange') as string;
    return `${format(currentStartDate, 'dd/MM/yyyy')} – ${format(currentEndDate, 'dd/MM/yyyy')}`;
  }, [currentStartDate, currentEndDate, t2]);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarAnchorRef = useRef<HTMLDivElement>(null);
  const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });

  const handleCalendarToggle = () => {
    if (!calendarOpen && calendarAnchorRef.current) {
      const rect = calendarAnchorRef.current.getBoundingClientRect();
      setCalendarPos({ top: rect.bottom + 4, left: rect.left });
      setDraftRange({
        startDate: currentStartDate ?? new Date(),
        endDate: currentEndDate ?? new Date(),
        key: 'selection',
      });
    }
    setCalendarOpen(prev => !prev);
  };

  const calendarRanges: Range[] = [draftRange];

  const handleClear = () => {
    reset(sessionsReportFilterDefaults);
    handleSubmit(submit)();
  };

  const shownCount = useMemo(
    () => (report?.groups ?? []).reduce((acc, g) => acc + g.sessions.length, 0),
    [report]
  );
  const totalCount = report?.kpis.totalSessions ?? 0;

  return (
    <Card sx={styles.card} elevation={0}>
      <Box sx={styles.topRow}>
        {/* Search */}
        <Box sx={styles.searchBox}>
          <IconSearch />
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                value={field.value ?? ''}
                placeholder={searchPlaceholder ?? (t2('filters.searchPlaceholder') as string)}
                style={styles.searchInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    lastSearchRef.current = field.value ?? '';
                    handleSubmit(submit)();
                  }
                }}
              />
            )}
          />
        </Box>

        {/* Course */}
        <Box sx={{ position: 'relative' }}>
          <DropdownTrigger
            icon={<IconCourse />}
            label={t2('filters.course') as string}
            value={courseLabel}
            onClick={setCourseAnchor}
          />
          <Menu
            anchorEl={courseAnchor}
            open={!!courseAnchor}
            onClose={() => setCourseAnchor(null)}
            PaperProps={{ sx: { maxHeight: 320, minWidth: 240 } }}
          >
            <MenuItem
              selected={!currentCourseId}
              onClick={() => {
                handleSelectChange('courseId', undefined);
                setCourseAnchor(null);
              }}
            >
              {t2('filters.allCourses')}
            </MenuItem>
            {courses.map(c => (
              <MenuItem
                key={c.id}
                selected={c.id === currentCourseId}
                onClick={() => {
                  handleSelectChange('courseId', c.id);
                  setCourseAnchor(null);
                }}
              >
                {c.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Status */}
        <Box sx={{ position: 'relative' }}>
          <DropdownTrigger
            icon={<IconStatus />}
            label={t2('filters.status') as string}
            value={statusLabel}
            onClick={setStatusAnchor}
          />
          <Menu
            anchorEl={statusAnchor}
            open={!!statusAnchor}
            onClose={() => setStatusAnchor(null)}
            PaperProps={{ sx: { minWidth: 200 } }}
          >
            <MenuItem
              selected={!currentStatus}
              onClick={() => {
                handleSelectChange('status', undefined);
                setStatusAnchor(null);
              }}
            >
              {t2('filters.allStatuses')}
            </MenuItem>
            {STATUS_OPTIONS.map(opt => (
              <MenuItem
                key={opt.value}
                selected={opt.value === currentStatus}
                onClick={() => {
                  handleSelectChange('status', opt.value);
                  setStatusAnchor(null);
                }}
              >
                {t2(opt.labelKey)}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Date Range Picker */}
        <Box sx={{ position: 'relative' }} ref={calendarAnchorRef}>
          <Button
            sx={styles.dropdownBtn(false)}
            onClick={handleCalendarToggle}
            disableRipple
            disableElevation
          >
            <IconCalendar />
            <Box component="span" sx={styles.dropdownLabel}>Rango:</Box>
            <Box component="span" sx={styles.dropdownValue}>
              {dateRangeLabel}
            </Box>
            <ChevronDown />
          </Button>
          {calendarOpen && (
            <>
              <Box
                sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1399 }}
                onClick={() => setCalendarOpen(false)}
              />
              <Box
                sx={{
                  position: 'fixed',
                  top: calendarPos.top,
                  left: calendarPos.left,
                  zIndex: 1400,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  '.rdrStartEdge, .rdrEndEdge': {
                    background: ACTIVE_FG,
                  },
                  '.rdrStartEdge ~ .rdrDayNumber span, .rdrEndEdge ~ .rdrDayNumber span': {
                    color: '#fff',
                  },
                  '.rdrInRange': {
                    background: ACTIVE_BG,
                  },
                  '.rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span': {
                    color: TEXT_STRONG,
                  },
                  '.rdrDayStartPreview, .rdrDayInPreview, .rdrDayEndPreview': {
                    borderColor: ACTIVE_FG,
                  },
                  '.rdrDayToday .rdrDayNumber span:after': {
                    background: ACTIVE_FG,
                  },
                  '.rdrNextPrevButton': {
                    background: ACTIVE_BG,
                  },
                  '.rdrNextPrevButton:hover': {
                    background: ACTIVE_BG,
                  },
                }}
              >
                <DateRange
                  ranges={calendarRanges}
                  onChange={handleDateRangeChange}
                  locale={es}
                  months={1}
                  showDateDisplay={false}
                />
                <Box sx={{ p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={handleApplyDateRange}
                    sx={{
                      bgcolor: ACTIVE_FG,
                      color: '#fff',
                      textTransform: 'none',
                      fontSize: 13,
                      fontWeight: 500,
                      px: 2,
                      py: 0.75,
                      '&:hover': { bgcolor: '#14a058' },
                    }}
                  >
                    {t2('filters.apply')}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ flex: '1 1 0%' }} />

        <Box component="button" type="button" onClick={handleClear} sx={styles.clearBtn}>
          {t2('filters.clear')}
        </Box>
      </Box>

      <Box sx={styles.bottomRow}>
        {!hideGroupBy && (
          <>
            <Box component="span" sx={styles.subLabel}>{t2('groupBy.label')}:</Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {GROUP_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  onClick={() => handleSelectChange('groupBy', opt.value)}
                  sx={styles.pill(currentGroup === opt.value)}
                  disableElevation
                  disableRipple
                >
                  {t2(opt.labelKey)}
                </Button>
              ))}
            </Box>
          </>
        )}
        <Box sx={{ flex: '1 1 0%' }} />
      </Box>
    </Card>
  );
}
